import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from "antd";

import { useModal } from "@/hooks/modals/useModal";
import { PlusOutlined } from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useCancelReasonBehavior } from "../hooks/useCancelReasonBehavior";
import { useFormEditable } from "../hooks/useFormEditable";
import { useSourcingFollowBehavior } from "../hooks/useSourcingFollowBehavior";
import type {
  MaterialItem,
  SourcingRequirementApplicationFormFields,
} from "../type";
import { CancelReasonModal } from "./CancelReasonModal";
import styles from "./MaterialFormFields.module.less";
import { SourcingFollowModal } from "./sourcingNodeFollow/SouringFollowModal";
import { defaultProductionMaterialSourcingNodes } from "./sourcingNodeFollow/node.config";
import MaterialSelector from "./MaterialSelector";
import { MaterialSapVo } from "@/apis/sap.api";
import { sourcingMethodOptions, sourcingOriginOptions } from "@/constants";
import { useSourcingRequirementQuery } from "../hooks/useSourcingRequirementQuery";
import { useTranslation } from "react-i18next";

export function ProductionMaterialFormFields() {
  const { t } = useTranslation("sourcingRequirementManagement");
  const { t: tCommon } = useTranslation("common");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { isMainFormDisabled, isSourcingByFormDisabled } = useFormEditable();

  const { props } = useModal("SOURCING_APPLICATION_20251202");
  const { mode, recordId } = props;

  const detail = useSourcingRequirementQuery(recordId);

  // 寻源申请已完成
  const isCompleted = detail?.reviewStatus === 4;

  const isStarted = mode === "started";

  const form = Form.useFormInstance<SourcingRequirementApplicationFormFields>();

  const {
    cancelReasonModalOpen,
    sourcingMaterialId,
    handleCancelModalCancel,
    handleCancelModalConfirm,
    handleCancelSourcingChange,
  } = useCancelReasonBehavior(form);

  const {
    sourcingFollowModalOpen,
    currentSourcingMaterialId,
    handleViewSourcingProgress,
    handleCloseSourcingFollowModal,
  } = useSourcingFollowBehavior();

  const handleSelectMaterial = (materials?: MaterialSapVo[]) => {
    if (materials && materials.length > 0) {
      let originalMaterialList = form.getFieldValue("materialList") || [];
      originalMaterialList = originalMaterialList.filter(
        (m: MaterialItem) => !!m.materialName,
      );
      const newMaterialList = materials.map((m) => ({
        materialCode: m.f101,
        materialName: m.f102 || m.f103,
        products: m.f131,
      }));
      form.setFieldsValue({
        materialList: [...originalMaterialList, ...newMaterialList],
      });
    }
  };

  const openViewSourcingProgress = (fieldIndex: number) => {
    const sourcingMaterialId = form.getFieldValue([
      "materialList",
      fieldIndex,
      "id",
    ]);
    handleViewSourcingProgress(sourcingMaterialId);
  };

  return (
    <Form.List name="materialList">
      {(fields, { add, remove }) => {
        const columns = createColumns({
          isCompleted,
          openViewSourcingProgress,
          hoverIndex,
          isMainFormDisabled,
          isSourcingByFormDisabled,
          remove,
          form,
          isStarted,
          handleCancelSourcingChange,
          t,
        });

        return (
          <>
            {!isMainFormDisabled && (
              <MaterialSelector onSelect={handleSelectMaterial} />
            )}
            <Table
              bordered
              dataSource={fields}
              pagination={false}
              scroll={{ x: 1200 }}
              onRow={(_, index) => ({
                onMouseEnter: () => setHoverIndex(index!),
                onMouseLeave: () => setHoverIndex(null),
              })}
              columns={columns}
            ></Table>

            <Button
              hidden={isMainFormDisabled}
              icon={<PlusOutlined />}
              onClick={add}
              variant="outlined"
              color="primary"
              className={styles.addBtn}
            >
              {tCommon("新增")}
            </Button>

            <CancelReasonModal
              sourcingMaterialId={sourcingMaterialId}
              open={cancelReasonModalOpen}
              onClose={handleCancelModalCancel}
              onOk={handleCancelModalConfirm}
            />

            <SourcingFollowModal
              currentSourcingMaterialId={currentSourcingMaterialId!}
              defaultSourcingNodes={defaultProductionMaterialSourcingNodes}
              open={sourcingFollowModalOpen}
              onClose={handleCloseSourcingFollowModal}
            />
          </>
        );
      }}
    </Form.List>
  );
}

type CreateColumnsFn = (params: {
  isCompleted: boolean;
  openViewSourcingProgress: (fieldIndex: number) => void;
  hoverIndex: number | null;
  isMainFormDisabled: boolean;
  isSourcingByFormDisabled: boolean;
  remove: (index: number) => void;
  form: FormInstance<SourcingRequirementApplicationFormFields>;
  isStarted: boolean;
  handleCancelSourcingChange: (
    isChecked: boolean,
    index: number,
    sourcingMaterialId: number,
  ) => void;
  t: (key: string) => string;
}) => TableColumnsType;

const createColumns: CreateColumnsFn = (params) => {
  const { isCompleted, form } = params;

  const materialList = form.getFieldValue("materialList");

  const showCancelReason = materialList.some(
    (m: MaterialItem) => m.isSourcingCancel && !!m.cancelReason,
  );

  let columns: TableColumnsType = initColumns(params);

  // 受原型字段顺序影响, 不能和后面 isCompleted 同时添加
  if (isCompleted) {
    columns = [...columns, ...addLastColumns(params)];
  }

  columns = [...columns, ...addCancelSourcingColumn(params)];

  if (showCancelReason) {
    columns = [...columns, ...addCancelReasonColumn(params)];
  }

  // 顺序添加
  if (isCompleted) {
    columns = [...columns, ...addSoucingExecutionColumn(params)];
  }

  return columns;
};

// 初始化列
const initColumns: CreateColumnsFn = ({
  hoverIndex,
  isMainFormDisabled,
  remove,
  t,
}) => {
  const columns: TableColumnsType = [
    {
      title: t("序号"),
      dataIndex: "index",
      width: 80,
      align: "center",
      fixed: "left",
      render: (_, __, index) => {
        const isHover = hoverIndex === index;
        const canRemove = isHover && !isMainFormDisabled;
        return canRemove ? (
          <div className={styles.removeIcon} onClick={() => remove(index)}>
            <Trash2 size={20} />
          </div>
        ) : (
          index + 1
        );
      },
    },
    {
      title: t("物料编号"),
      dataIndex: "materialCode",
      align: "center",
      width: 100,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "materialCode"]}
          rules={[
            {
              required: true,
              message: t("请输入编号"),
            },
          ]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("物料名称")}</span>,
      dataIndex: "materialName",
      align: "center",
      width: 100,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "materialName"]}
          rules={[
            {
              required: true,
              message: t("请输入物料名称"),
            },
          ]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: t("年采购数量"),
      dataIndex: "count",
      align: "center",
      width: 120,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "count"]}
          rules={[
            {
              required: true,
              message: t("请输入年采购数量"),
            },
          ]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("相关产品")}</span>,
      dataIndex: "products",
      align: "center",
      width: 100,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "products"]}
          rules={[
            {
              required: true,
              message: t("请输入相关产品"),
            },
          ]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("要求完成日期")}</span>,
      dataIndex: "completeDate",
      align: "center",
      width: 180,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "completeDate"]}
          rules={[
            {
              required: true,
              message: t("请输入要求完成日期"),
            },
          ]}
          noStyle
        >
          <DatePicker />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("寻源原因")}</span>,
      dataIndex: "sourcingReason",
      align: "center",
      width: 100,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          noStyle
          name={[field.name, "sourcingReason"]}
          rules={[
            {
              required: true,
              message: t("请输入寻源原因"),
            },
          ]}
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("寻源要求")}</span>,
      dataIndex: "sourcingRequirements",
      align: "center",
      width: 100,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "sourcingRequirements"]}
          rules={[
            {
              required: true,
              message: t("请输入寻源要求"),
            },
          ]}
          noStyle
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: t("寻源优先级"),
      dataIndex: "sourcingPrioritize",
      align: "center",
      width: 180,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "sourcingPrioritize"]}
          noStyle
        >
          <Radio.Group
            options={[
              { value: 1, label: t("紧急") },
              { value: 2, label: t("一般") },
            ]}
          />
        </Form.Item>
      ),
    },
  ];

  return columns;
};

// 取消寻源列
const addCancelSourcingColumn: CreateColumnsFn = ({
  isCompleted,
  isStarted,
  handleCancelSourcingChange,
  form,
  t,
}) => {
  const columns: TableColumnsType = [
    {
      title: t("取消寻源"),
      dataIndex: "isSourcingCancel",
      align: "center",
      width: 100,
      render: (_, field, index) => {
        /**
         * 同时满足以下条件时，允许编辑取消寻源复选框
         * 1. 当前审核状态为“完成”（reviewStatus === "4"）
         * 2. mode 为 started
         * 3. 该物料的取消寻源状态尚未设置
         */
        const isSourcingCancel = form.getFieldValue([
          "materialList",
          field.name,
          "isSourcingCancel",
        ]);
        const isEditable = isCompleted && isStarted && !isSourcingCancel;

        return (
          <>
            <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
              name={[field.name, "isSourcingCancel"]}
              noStyle
              valuePropName="checked"
            >
              <Checkbox
                onChange={(e) =>
                  handleCancelSourcingChange(
                    e.target.checked,
                    index,
                    form.getFieldValue(["materialList", field.name, "id"]),
                  )
                }
                disabled={!isEditable}
              ></Checkbox>
            </Form.Item>

            <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
              name={[field.name, "id"]}
              hidden
            ></Form.Item>
          </>
        );
      },
    },
  ];
  return columns;
};

// 添加"寻源执行"列
const addSoucingExecutionColumn: CreateColumnsFn = ({
  openViewSourcingProgress,
  t,
}) => {
  const columns: TableColumnsType = [
    {
      title: t("寻源执行"),
      dataIndex: "soucingExecution",
      align: "center",
      fixed: "right",
      width: 150,
      render: (_, field) => {
        return (
          <Button
            disabled={false}
            onClick={() => openViewSourcingProgress(field.name)}
            variant="link"
            color="primary"
          >
            {t("查看寻源进度")}
          </Button>
        );
      },
    },
  ];

  return columns;
};

// 寻源申请完成后续要添加的列
const addLastColumns: CreateColumnsFn = ({ isSourcingByFormDisabled, t }) => {
  const columns: TableColumnsType = [
    {
      title: t("寻源来源"),
      dataIndex: "sourcingOrigin",
      align: "center",
      width: 120,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "sourcingOrigin"]}
            noStyle
          >
            <Select
              options={sourcingOriginOptions}
              style={{ width: "100%" }}
              placeholder={t("请选择")}
              disabled={isSourcingByFormDisabled}
            ></Select>
          </Form.Item>
        );
      },
    },
    {
      title: t("寻源方法"),
      dataIndex: "sourcingMethod",
      align: "center",
      width: 120,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "sourcingMethod"]}
            noStyle
          >
            <Select
              options={sourcingMethodOptions}
              style={{ width: "100%" }}
              placeholder={t("请选择")}
              disabled={isSourcingByFormDisabled}
            ></Select>
          </Form.Item>
        );
      },
    },
    {
      title: t("计划开始时间"),
      dataIndex: "planStartTime",
      align: "center",
      width: 180,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "planStartTime"]}
          noStyle
        >
          <DatePicker disabled={isSourcingByFormDisabled} />
        </Form.Item>
      ),
    },
    {
      title: t("计划结束时间"),
      dataIndex: "planEndTime",
      align: "center",
      width: 180,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "planEndTime"]}
          noStyle
        >
          <DatePicker disabled={isSourcingByFormDisabled} />
        </Form.Item>
      ),
    },
  ];

  return columns;
};

// 添加"取消原因"列
const addCancelReasonColumn: CreateColumnsFn = ({ t }) => {
  const columns: TableColumnsType = [
    {
      title: t("取消原因"),
      dataIndex: "cancelReason",
      align: "center",
      width: 140,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            noStyle
            name={[field.name, "cancelReason"]}
          >
            <Input.TextArea rows={1} placeholder={t("请输入")} disabled />
          </Form.Item>
        );
      },
    },
  ];
  return columns;
};
